import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { ChildComponent } from "../../child-component";
import { NotificationClientService } from "../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import * as moment from "moment";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import * as Enumerable from "linq";
import { VesselHelper, RatingsSummaryModel } from "../../../../../models/vessels/vessel.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { ManageVesselService } from "../../../../../services/vessels/manage-vessel.service";
import { SafetyRatingReviewClientModel, SafetyRatingReviewStatusEnum, SafetyRatingReviewOutcomeStepEnum, SafetyRatingReviewTaskClientModel } from "../../../../../models/vetting/vetting.models";
import { Reflection } from "../../../../../utilities/reflection";

@autoinject
@transient()
@useView('views/pages/modules/safety-score-review/tabs/safety-score-review-overview.html')
export class SafetyScoreReviewOverview extends ChildComponent<SafetyRatingReviewClientModel> {

    public isEditingEmail: boolean;
    public originalEmail: string;
    private safetyRatingReviewStatuses: LookupValue[];
    public isRightShipUser: boolean;
    public isShowMoreSectionDetails: boolean;
    public isShowMoreSectionStatus: boolean;
    public isShowMoreSectionRelatedActivities: boolean;
    public viewCorrespondence: any;
    public canChangeShipManagerEmails: boolean;
    public reassignment: SafetyRatingReviewStatusEnum = SafetyRatingReviewStatusEnum.SafetyRatingReviewReassignment;
    public reloadItemFunc: any;
    public canContactRightshipOperations: boolean;

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item),
        Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewTasks),
        Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewTasks.length))
    get pendingRules() {
        return this.item.safetyRatingReviewTasks.filter(task => !task.vetSuperSafetyRatingReviewTaskOutcomeId || task.vetSuperSafetyRatingReviewTaskOutcomeId <= 0).length;
    }

    attached() {
        this.signalClientService.subscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        this.refreshRating({ vesselIds: [this.item.vesselId] });
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
    }

    ratingModel: RatingsSummaryModel;
    private refreshRating = async (param) => {
        if (param && param.vesselIds && param.vesselIds.filter(x => x == this.item.vesselId).length >= 1) {
            try {
                const response = await this.manageVesselService.getRatingsModel(this.item.vesselId);
                this.ratingModel = response;
            } catch (e) {
                console.error("Failed to refresh rating", e);
            }
        }
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item),
        Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.selectedOutcomes),
        Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.selectedOutcomes.length),
        Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewTasks),
        Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewTasks.length))
    get safetyRatingReviewTasks() {
        const tasks = {};
        if (this.item) {
            const filter = this.selectedOutcomes.length > 0;
            let safetyRatingReviewTasks = this.item.safetyRatingReviewTasks;
            const outcomeIds = this.selectedOutcomes
            if (filter) {
                const pending = outcomeIds.filter(x => x == -1).length > 0;
                safetyRatingReviewTasks = safetyRatingReviewTasks.filter(task => (pending && (!task.vetSuperSafetyRatingReviewTaskOutcomeId || task.vetSuperSafetyRatingReviewTaskOutcomeId <= 0)) || outcomeIds.indexOf(task.vetSuperSafetyRatingReviewTaskOutcomeId) > -1)
            }
            const items = Enumerable.from(safetyRatingReviewTasks).groupBy(x => x.category ? x.category : "No Category")
                .toArray();
            for (const item of items) {
                tasks[item.key()] = item.toArray();
            }
        }
        return tasks;
    }


    get passed() {
        if (this.item && this.item.safetyRatingReviewTasks) {
            return this.item.safetyRatingReviewTasks.filter(x => this.getOutcome(x) == SafetyRatingReviewOutcomeStepEnum.Pass).length;
        }
        return 0;
    }

    get failed() {
        if (this.item && this.item.safetyRatingReviewTasks) {
            return this.item.safetyRatingReviewTasks.filter(x => this.getOutcome(x) == SafetyRatingReviewOutcomeStepEnum.Fail).length;
        }
        return 0;
    }

    @computedFrom("item", "item.safetyRatingReviewStatusId")
    get shouldShowContactRS() {
        return this.item &&
            (this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.AwaitingResponse ||
                this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.InProgress ||
                this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.New ||
                this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.PendingReview ||
                this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Processing ||
                this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.SafetyRatingReviewReassignment);
    }

    @computedFrom("item", "item.safetyRatingReviewStatusId")
    get shouldShowSafetyScoreAndGhg() {
        return this.item && (this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Accepted2 ||
            this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.AcceptedFromSystem ||
            this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Canceled ||
            this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Rejected);
    }

    get awaitingResponse() {
        return this.item && (this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.AwaitingResponse);
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.safetyRatingReviewStatuses),
    Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.safetyRatingReviewStatuses.length),
    Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item),
    Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewStatusId))
    get safetyRatingReviewStatus() {
        if (this.safetyRatingReviewStatuses && this.item)
            return this.safetyRatingReviewStatuses.filter(x => x.id == this.item.safetyRatingReviewStatusId)[0];

        return null;
    }

    @computedFrom("item", "item.built")
    get built() {
        const date = moment(this.item.built);
        const yearAge = VesselHelper.getAgeFromDate(date);
        return yearAge;
    }

    @computedFrom("item", "item.safetyRatingReviewStatusHistory", "item.safetyRatingReviewStatusHistory.length", "item.vetStatusId")
    get lastSafetyRatingReviewStatusHistory() {
        return this.item.safetyRatingReviewStatusHistory[0];
    }

    @computedFrom("item", "item.initialEmailsForCorrespondence", "item.initialEmailsForCorrespondence.length")
    get shipManagerEmails() {
        if (this.item.initialEmailsForCorrespondence)
            return this.item.initialEmailsForCorrespondence.filter(x => x.isShipManagerEmail);
        return [];
    }

    @computedFrom("item", "item.safetyRatingReviewStatusId")
    get notNewOrProcessing() {
        return this.item && this.item.safetyRatingReviewStatusId != SafetyRatingReviewStatusEnum.Processing && this.item.safetyRatingReviewStatusId != SafetyRatingReviewStatusEnum.New;
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item),
    Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewTasks),
    Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewTasks.length))
    get safetyRatingReviewTaskKeys() {
        if (this.item)
            return Enumerable.from(this.item.safetyRatingReviewTasks).select(x => x.category ? x.category : "No Category").distinct().toArray();
        return [];
    }

    constructor(private signalClientService: SignalClientService, private manageVesselService: ManageVesselService,
        notificationsService: NotificationClientService, lookupClientService: LookupClientService,
        private userClaimsService: UserClaimsService, private dialogClientService: DialogClientService,
        i18N: I18N, private router: Router
    ) {
        super(notificationsService, i18N);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.SafetyRatingReviewStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.safetyRatingReviewStatuses = x.activeValues;
        });
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canChangeShipManagerEmails = this.userClaimsService.hasPermission(Claims.CanChangeShipManagerEmails);
        this.canContactRightshipOperations = this.userClaimsService.hasPermission(Claims.CanContactRightshipOperations);
    }

    protected onActivated() {
        this.setCategories();
    }

    getTitle(taskGroup) {
        const x = Number(taskGroup) as SafetyRatingReviewOutcomeStepEnum;
        switch (x) {
            case SafetyRatingReviewOutcomeStepEnum.Pass:
                return "Passed Rules";
            case SafetyRatingReviewOutcomeStepEnum.Fail:
                return "Failed Rules";
        }
    }

    filterClick(outcome: { id: number; name: string }) {
        const index = this.selectedOutcomes.indexOf(outcome.id);
        if (index >= 0) {
            this.selectedOutcomes.splice(index, 1);
        }
        else {
            this.selectedOutcomes.push(outcome.id);
        }
    }

    navigateToVessel() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselDetails, { id: this.item.vesselId });
    }

    private getOutcome(x: SafetyRatingReviewTaskClientModel) {
        return x.vetSuperSafetyRatingReviewTaskOutcomeId ? x.vetSuperSafetyRatingReviewTaskOutcomeId : x.safetyRatingReviewTaskOutcomeId;
    }

    selectedOutcomes: number[] = [];
    categories: { id: number; name: string }[] = [];
    
    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item),
    Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewTasks),
    Reflection.getPropertyPath<SafetyScoreReviewOverview>(z => z.item.safetyRatingReviewTasks.length))
    get outcomes(): { id: number; name: string }[] {
        const list = [];
        if (this.item && this.item.safetyRatingReviewTasks) {
            const safetyRatingReviewTasks: SafetyRatingReviewTaskClientModel[] = this.item.safetyRatingReviewTasks;
            const pending = safetyRatingReviewTasks.filter(task => !task.vetSuperSafetyRatingReviewTaskOutcomeId || task.vetSuperSafetyRatingReviewTaskOutcomeId <= 0).length;
            const passed = safetyRatingReviewTasks.filter(task => task.vetSuperSafetyRatingReviewTaskOutcomeId == SafetyRatingReviewOutcomeStepEnum.Pass).length;
            const failed = safetyRatingReviewTasks.filter(task => task.vetSuperSafetyRatingReviewTaskOutcomeId == SafetyRatingReviewOutcomeStepEnum.Fail).length;

            list.push({ id: -1, name: `Pending (${pending})` });
            list.push({ id: SafetyRatingReviewOutcomeStepEnum.Pass, name: `Passed (${passed})` });
            list.push({ id: SafetyRatingReviewOutcomeStepEnum.Fail, name: `Failed (${failed})` });
        }

        return list;
    }

    setCategories() {
        const safetyRatingReviewTasks: SafetyRatingReviewTaskClientModel[] = Enumerable.from(this.safetyRatingReviewTasks).selectMany(x => <SafetyRatingReviewTaskClientModel[]>x.value).toArray();
        this.categories = Enumerable.from(safetyRatingReviewTasks.map(x => {
            return {
                id: x.categoryId,
                name: x.category
            };
        })).groupBy(x => x.id).select(x => x.firstOrDefault()).where(x => x != null).toArray();
    }

    showContactVetSuperDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddCorrespondenceDialog, {
            entityId: this.item.safetyRatingReviewId,
            entityTypeId: EntityTypes.SafetyRatingReview
        }, true, false, false, "right");
    }

    @computedFrom("lastSafetyRatingReviewStatusHistory", "lastSafetyRatingReviewStatusHistory.user")
    get lastSafetyRatingReviewStatusHistoryUser() {
        const systemStatuses = [
            SafetyRatingReviewStatusEnum.Processing,
            SafetyRatingReviewStatusEnum.New,
            SafetyRatingReviewStatusEnum.PendingReview
        ];
        let user = this.lastSafetyRatingReviewStatusHistory ? this.lastSafetyRatingReviewStatusHistory.user : null;
        const statusId = this.lastSafetyRatingReviewStatusHistory ? this.lastSafetyRatingReviewStatusHistory.statusId : null;
        if (!user && systemStatuses.indexOf(statusId) > -1) user = "System";
        return user;
    }

    showMoreSectionDetails() {
        this.isShowMoreSectionDetails = !this.isShowMoreSectionDetails;
    }

    showMoreSectionStatus() {
        this.isShowMoreSectionStatus = !this.isShowMoreSectionStatus;
    }

    showMoreSectionRelatedActivities() {
        this.isShowMoreSectionRelatedActivities = !this.isShowMoreSectionRelatedActivities;
    }

    openContactDetailsDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ContactDetailsDialog, {
            model: this.item,
            onConfirm: null
        }, true, false, false,)
    }

    //PX-9656
    getStatusOutcomeLabel(status: string) {
        status = status.toLocaleLowerCase();
        if(status == "acceptable (second review)" || status == "accepted from system" || status == "unacceptable"){
            return "Outcome";
        }
        else {
            return "Current status";
        }
    }

    getStatus(status: string) {
        status = status.split(' ').join('-').toLowerCase();

        if (status.indexOf('(') != -1) {
            status = status.substr(0, status.indexOf('(') - 1);
        }

        return status;
    }
}
